'use client';

import { useStorageUrl } from '@/hooks/use-storage-url';
import { pickUserAvatarSource, userAvatarSeed } from '@/lib/user-avatar-source';
import Avvvatars from 'avvvatars-react';
import { useLocale } from 'next-intl';
import { memberDisplayNameFallback } from '@/i18n/user-display-name-copy';
import { userDisplayName } from '@/lib/user-display-name';

export type BasicUser = {
  _id?: string;
  email?: string | null;
  name?: string | null;
  nameSetByUser?: boolean;
  image?: string | null;
  imageStorageKey?: string | null;
};

export function UserAvatarDisplay({
  user,
  displayName,
  size = 32,
  className,
}: {
  user: BasicUser | null | undefined;
  /** Already-filtered display projection; never pass an unfiltered identity name. */
  displayName?: string;
  size?: number;
  className?: string;
}) {
  // The server's public `image` URL first (another member's profile-photo key
  // never resolves for the viewer), then the R2 key, then a legacy storageId.
  // Keys route through the authenticated resolver; the client never derives a
  // public CDN URL itself.
  const member = memberDisplayNameFallback(useLocale());
  const imageUrl = useStorageUrl(pickUserAvatarSource(user));

  const avatarValue = displayName?.trim() || userDisplayName(user, member);

  if (imageUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={imageUrl}
        alt={displayName?.trim() || userDisplayName(user, member)}
        width={size}
        height={size}
        className={`inline-block rounded-full object-cover ${className ?? ''}`}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      className={`relative inline-flex overflow-hidden rounded-full ${className ?? ''}`}
      style={{ width: size, height: size }}
      aria-label={displayName?.trim() || userDisplayName(user, member)}
      role="img"
    >
      <Avvvatars
        value={userAvatarSeed(user, avatarValue)}
        style="shape"
        size={size}
        radius={999}
        shadow={false}
      />
    </span>
  );
}
